import React from 'react'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Link from 'components_new/atoms/Link'
import Text from 'components_new/atoms/Text'

import { useTranslation } from 'language/translator'

import {
  BANNER_HEIGHT,
  HEADER_FONT,
  LEVEL_STYLES,
  StatusBannerItem
} from './lib'

interface CollapsedRowProps {
  banner: StatusBannerItem
  hiddenCount: number
  onToggleExpanded?: () => void
  onClose?: (id: string) => void
}

const CollapsedRow = (props: CollapsedRowProps) => {
  const { banner, hiddenCount, onToggleExpanded, onClose } = props
  const { t } = useTranslation()

  const styles = LEVEL_STYLES[banner.level]
  const isImportant = banner.level === 'important'

  return (
    <Box
      sx={{
        height: BANNER_HEIGHT,
        bgcolor: styles.bg,
        color: styles.text,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.25
      }}
    >
      {isImportant ? (
        <Box
          sx={{
            flex: 'none',
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: styles.iconChipBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon name="Info" sx={{ fontSize: 16, color: styles.icon }} />
        </Box>
      ) : (
        <Icon name="Info" sx={{ fontSize: 19, color: styles.icon }} />
      )}

      <Text
        component="span"
        noWrap
        sx={{ minWidth: 0, fontSize: 14, color: styles.text }}
      >
        <Box component="span" sx={{ fontWeight: 600 }}>
          {banner.title}
        </Box>
        <Box component="span" sx={{ opacity: 0.6 }}>
          {` · ${banner.description}`}
        </Box>
      </Text>

      <Box sx={{ flex: 1 }} />

      {hiddenCount > 0 ? (
        <Box
          component="button"
          onClick={onToggleExpanded}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            bgcolor: styles.counterBg,
            color: styles.text,
            border: 'none',
            borderRadius: 9999,
            px: 1.5,
            py: 0.625,
            fontFamily: HEADER_FONT,
            fontWeight: 600,
            fontSize: 13,
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}
        >
          {t(
            'status_banner',
            hiddenCount === 1 ? 'more_messages_one' : 'more_messages_other',
            false,
            { count: String(hiddenCount) }
          )}
          <Icon name="ExpandMore" sx={{ fontSize: 15 }} />
        </Box>
      ) : banner.href ? (
        isImportant ? (
          <Link
            href={banner.href}
            newTab
            target="_blank"
            rel="noreferrer"
            underline="none"
            sx={{
              bgcolor: 'white.main',
              color: 'black.main',
              borderRadius: 9999,
              px: 1.75,
              py: 0.75,
              fontFamily: HEADER_FONT,
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: 'nowrap'
            }}
          >
            {banner.linkLabel || t('status_banner', 'read_more')}
          </Link>
        ) : (
          <>
            <Link
              href={banner.href}
              newTab
              target="_blank"
              rel="noreferrer"
              underline="none"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: 'black.main',
                fontFamily: HEADER_FONT,
                fontWeight: 600,
                fontSize: 13.5,
                whiteSpace: 'nowrap'
              }}
            >
              {banner.linkLabel || t('status_banner', 'read_more')}
            </Link>
            {onClose ? (
              <Box sx={{ width: '1px', height: 18, bgcolor: styles.divider }} />
            ) : null}
          </>
        )
      ) : null}

      {onClose ? (
        <IconButton
          onClick={() => onClose(banner.id)}
          size="small"
          sx={{ color: styles.closeColor }}
        >
          <Icon name="Close" sx={{ fontSize: 18, color: 'inherit' }} />
        </IconButton>
      ) : null}
    </Box>
  )
}

export default CollapsedRow
